import {useMemo} from 'react'
import {StyleSheet, View} from 'react-native'
import {type AppBskyActorDefs, AtUri} from '@atproto/api'
import {useLingui} from '@lingui/react/macro'

import {gradeLabel} from '#/lib/curriculum'
import {makeProfileLink} from '#/lib/routes/links'
import {
  type Classroom,
  type ClassroomContent,
  type ClassroomLevelArchive,
  findMember,
  useClassroomActor,
} from '#/state/classrooms'
import {
  useAcknowledgeAnnouncementMutation,
  useLeaveClassroomMutation,
} from '#/state/queries/classrooms'
import {PostMeta} from '#/view/com/util/PostMeta'
import {PreviewableUserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {CapsuleFeedEmbed} from '#/components/capsules/CapsuleFeedEmbed'
import {InlineLinkText} from '#/components/Link'
import {RichText} from '#/components/RichText'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {LearnerLevelButton} from './LearnerLevelDialog'
import {showClassroomError} from './showClassroomError'
import {useClassroomCapsule} from './useClassroomCapsule'

/**
 * One piece of work in a classroom, shaped like a post.
 *
 * A classroom's work is shown the way a profile shows a person's — the same
 * avatar, the same name / handle / time line, the same body text, and, for a
 * capsule, the same capsule the feed draws — because a reader should not have to
 * learn a second way of reading a post to look at a classroom. What it does *not*
 * carry is the action row: there is no like, no repost and no reply, because nothing
 * here has been published to the network — and with no menu to hold it, no icon
 * button either, only a named action.
 * When the same work also lives on its author's profile, the card says so and
 * links there, and that copy is a real post with everything a post has.
 */
export function ClassroomContentCard({
  classroom,
  content,
  onRemove,
}: {
  classroom: Classroom
  content: ClassroomContent
  onRemove?: (id: string) => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  // The server says who may take an entry out of the room.
  const canRemove = !!onRemove && !!content.viewerCanManage

  /**
   * The author, in the shape the post components expect. Everything needed is
   * already on the entry — a classroom keeps its own copy of who wrote what.
   */
  const author = useMemo<AppBskyActorDefs.ProfileViewBasic>(
    () => ({
      did: content.authorDid,
      handle: content.authorHandle,
      displayName: content.authorName,
      avatar: content.authorAvatar,
    }),
    [
      content.authorAvatar,
      content.authorDid,
      content.authorHandle,
      content.authorName,
    ],
  )

  const postPath = useMemo(() => {
    if (!content.postUri) return undefined
    const urip = new AtUri(content.postUri)
    return makeProfileLink(
      {did: urip.host, handle: urip.host},
      'post',
      urip.rkey,
    )
  }, [content.postUri])

  return (
    <View testID={`classroomContent-${content.id}`} style={styles.layout}>
      <View style={styles.layoutAvi}>
        <PreviewableUserAvatar size={42} profile={author} />
      </View>
      <View style={[a.flex_1]}>
        <PostMeta
          author={author}
          moderation={undefined}
          timestamp={content.createdAt}
          postHref={postPath ?? makeProfileLink(author)}
        />

        {content.announcement === 'level' ? (
          <LevelAnnouncement classroom={classroom} content={content} />
        ) : (
          <>
            {content.kind === 'capsule' && content.capsuleTitle ? (
              <Text
                emoji
                style={[a.text_md, a.font_bold, a.leading_tight, a.pb_xs]}>
                {content.capsuleTitle}
              </Text>
            ) : null}

            {content.text ? (
              <RichText
                testID={`classroomContentText-${content.id}`}
                value={content.text}
                authorHandle={content.authorHandle}
                enableTags
                selectable
                style={[a.text_md, a.leading_snug]}
              />
            ) : null}

            {/*
              The capsule itself, drawn the way every capsule in this app is drawn: the
              film, what it is worth, and the way in. A classroom's capsule is a piece of
              work like any other in this list, and a line of text with a link beside it
              was not that — it was the room's own invention, and the only capsule in the
              app that did not look like a capsule.
            */}
            {content.kind === 'capsule' && content.capsuleUri ? (
              <View style={[a.pt_sm]}>
                <ClassroomCapsuleEmbed
                  content={content}
                  capsuleUri={content.capsuleUri}
                />
              </View>
            ) : null}
          </>
        )}

        <View
          style={[
            a.flex_row,
            a.align_center,
            a.gap_sm,
            a.flex_wrap,
            {paddingTop: 6, minHeight: 28},
          ]}>
          {content.visibility === 'members' && !content.announcement ? (
            <Tag>
              {content.capsuleUri ? l`Classroom only` : l`Members only`}
            </Tag>
          ) : null}
          {/*
            A capsule the room owns has no post to open — this row is the only
            way in from the Posts tab, where a capsule is a piece of work like
            any other. Everything else here is a view of a real post, and its own
            link carries it.
          */}
          {postPath ? (
            <InlineLinkText
              testID={`classroomContentOnProfile-${content.id}`}
              to={postPath}
              label={l`Open this on its author's profile`}
              style={[a.text_sm]}>
              {l`Also on their profile`}
            </InlineLinkText>
          ) : null}
          <View style={[a.flex_1]} />
          {canRemove ? (
            <Button
              testID={`classroomRemoveContent-${content.id}`}
              label={l`Remove this from the classroom`}
              color="secondary"
              size="small"
              onPress={() => onRemove?.(content.id)}>
              <ButtonText>{l`Remove`}</ButtonText>
            </Button>
          ) : null}
        </View>
      </View>
    </View>
  )
}

/**
 * The room telling the members it concerns that it now teaches something else.
 *
 * A classroom changes its level once a month at most, and nobody is taken out of
 * it when it does: the members the change leaves behind are told here, in the room
 * they are already in. It is shown to *them* and to nobody else
 * (`announcementIsForMember`), so a member who studies what the room teaches now
 * never sees it at all.
 *
 * What it says, and the two answers it offers, follow the person reading it —
 * because the room has three kinds of member and only one of them has a level to
 * move:
 *
 * - A **learner** is given the level they can move to: *you can change your
 *   level — or leave the classroom*, with both answers as buttons. Changing the
 *   level is what makes the card go away, because afterwards they are a member it
 *   concerns no longer.
 * - A **teacher or a parent** has no level to move, and is asked the quieter
 *   question instead: if this content does not suit you, you can leave the room.
 *   Their two answers are *keep* — which writes the answer down and takes the
 *   card away (`useAcknowledgeAnnouncementMutation`) — and *leave*.
 * - The **school** that runs the room is not asked anything.
 *
 * The server writes the announcement when the level moves, so this card reads
 * what was announced rather than what the room says today: a room that moves
 * again in a month's time gets a second announcement, and this one keeps saying
 * what it said.
 */
function LevelAnnouncement({
  classroom,
  content,
}: {
  classroom: Classroom
  content: ClassroomContent
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const actor = useClassroomActor()
  const leave = useLeaveClassroomMutation(classroom.id)
  const acknowledge = useAcknowledgeAnnouncementMutation(classroom.id)
  const member = findMember(classroom, actor?.did)
  const isOwner = !!actor && actor.did === classroom.ownerDid
  const grade = content.announcedGrade
    ? gradeLabel(content.announcedGrade)
    : null
  const levelText = [content.announcedLevel, grade ? l(grade) : null]
    .filter(Boolean)
    .join(' · ')

  /*
   * The seat decides the sentence. A teacher or a parent moves neither their
   * level nor their year — there is nothing for them to move — so they are asked
   * the room's quieter question. Everybody else is a learner here, including a
   * seat the app cannot put a name to: a learner it cannot identify must not be
   * left without the way to move.
   */
  const keepOrLeave = member?.role === 'teacher' || member?.role === 'parent'

  const onLeave = () => {
    if (!actor || leave.isPending) return
    leave.mutate(undefined, {
      onSuccess: () => Toast.show(l`You left ${classroom.name}`),
      onError: error => showClassroomError(i18n, error),
    })
  }

  const onKeep = () => {
    if (!actor || acknowledge.isPending) return
    acknowledge.mutate(
      {contentId: content.id},
      {
        onSuccess: () => Toast.show(l`You kept ${classroom.name}`),
        onError: error => showClassroomError(i18n, error),
      },
    )
  }

  return (
    <View
      testID={`classroomLevelAnnouncement-${content.id}`}
      style={[a.rounded_md, a.p_md, a.gap_sm, t.atoms.bg_contrast_25]}>
      <Text style={[a.text_md, a.font_semi_bold]}>
        {l`This classroom changed its level`}
      </Text>
      <Text style={[a.text_md, a.leading_snug]}>
        {content.announcedLevel
          ? l`It now teaches ${levelText}.`
          : l`It is now open to every learner, whatever year they are in.`}
      </Text>
      {isOwner ? null : (
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          {keepOrLeave
            ? l`If the content of this level does not suit you, you can leave the classroom — it keeps your notifications, and the content you see, relevant to you.`
            : l`You can change your level — or leave the classroom.`}
        </Text>
      )}
      {isOwner || !actor ? null : (
        <View style={[a.flex_row, a.gap_sm, a.pt_xs]}>
          {keepOrLeave ? (
            <Button
              testID={`classroomAnnouncementKeep-${content.id}`}
              label={l`Keep ${classroom.name}`}
              color="primary"
              size="small"
              onPress={onKeep}>
              <ButtonText>{l`Keep this classroom`}</ButtonText>
            </Button>
          ) : (
            <LearnerLevelButton
              testID={`classroomAnnouncementChangeLevel-${content.id}`}
              label={l`Change level`}
              classroomId={classroom.id}
            />
          )}
          <Button
            testID={`classroomAnnouncementLeave-${content.id}`}
            label={l`Leave ${classroom.name}`}
            color="secondary"
            size="small"
            onPress={onLeave}>
            <ButtonText>{l`Leave`}</ButtonText>
          </Button>
        </View>
      )}
    </View>
  )
}

/**
 * A period of the room, and the work written during it — folded away.
 *
 * A level change does not take the room's work with it. Everything written before
 * the move stays where it was written, and the room *says so*: a line in the feed —
 * the same hairline the list draws between two posts, because this *is* a line
 * between two posts — names what the room changed from and to, says which level the
 * work under it belongs to, and offers it back a page at a time. That is the
 * difference between an archive and a deletion: a school that moved its level by
 * mistake can read what it moved away from without having to trust that nothing
 * was lost.
 *
 * It is a divider and not a card on purpose. A boxed card in the middle of a feed
 * reads as one more piece of work; the feed already has a shape for *the posts
 * under this belong together*, and that shape is a line across it.
 *
 * The work behind it comes back on its own the moment the room returns to that
 * level, because nothing here is a flag to clear: the divider is drawn only while
 * the room teaches something else than the work was written at
 * (`contentFromAnotherLevel`).
 */
export function ClassroomLevelArchiveCard({
  group,
  expanded,
  onToggle,
  renderItem,
}: {
  group: ClassroomLevelArchive<ClassroomContent>
  expanded: boolean
  onToggle: () => void
  renderItem: (item: ClassroomContent, index: number) => React.ReactNode
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const grade = group.grade ? gradeLabel(group.grade) : null
  const levelText = [group.level, grade ? l(grade) : null]
    .filter(Boolean)
    .join(' · ')
  const toGrade = group.toGrade ? gradeLabel(group.toGrade) : null
  const toText = group.to
    ? [group.to, toGrade ? l(toGrade) : null].filter(Boolean).join(' · ')
    : null

  const oldPosts =
    group.items.length === 1
      ? l`See the old post`
      : l`See the ${group.items.length} old posts`

  return (
    <View testID={`classroomLevelArchive-${group.key}`}>
      {/*
        The line itself: the list's own separator, drawn across the whole width,
        with the room's sentence and its one control under it. A reader who is
        scrolling sees a break in the feed and a reason for it, not a card.
      */}
      <View style={[a.border_t, t.atoms.border_contrast_low]} />
      <View style={[a.px_lg, a.py_md, a.gap_xs]}>
        <Text style={[a.text_sm, a.font_semi_bold]}>
          {toText
            ? l`The admin changed this classroom from ${levelText} to ${toText}.`
            : l`The admin changed this classroom.`}
        </Text>
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          {l`This content was in ${levelText}.`}
        </Text>
        <View style={[a.pt_xs, a.flex_row, a.justify_start]}>
          <Button
            testID={`classroomArchiveToggle-${group.key}`}
            label={expanded ? l`Hide the old posts` : oldPosts}
            color="secondary"
            size="small"
            onPress={onToggle}>
            <ButtonText>
              {expanded ? l`Hide the old posts` : oldPosts}
            </ButtonText>
          </Button>
        </View>
      </View>
      {expanded ? (
        <>
          <View style={[a.border_t, t.atoms.border_contrast_low]} />
          {group.items.map((item, index) => (
            <View key={item.id} testID={`classroomArchivedContent-${item.id}`}>
              {renderItem(item, index)}
            </View>
          ))}
          {/*
            The same control at the other end of the stack. A period can be a
            year of posts, and a fold that can only be closed from the top of it
            is a fold a reader has to scroll back through to close.
          */}
          <View style={[a.border_t, t.atoms.border_contrast_low]} />
          <View style={[a.px_lg, a.py_sm]}>
            <Button
              testID={`classroomArchiveHide-${group.key}`}
              label={l`Hide the old posts`}
              color="secondary"
              size="small"
              onPress={onToggle}>
              <ButtonText>{l`Hide the old posts`}</ButtonText>
            </Button>
          </View>
        </>
      ) : (
        <View style={[a.border_t, t.atoms.border_contrast_low]} />
      )}
    </View>
  )
}

/**
 * A capsule the room owns, drawn by the feed's own capsule embed.
 *
 * Split out so the hook that builds its post-shaped view is only ever called with
 * an address in hand: an entry with no capsule has nothing to build one from, and
 * a hook cannot be called conditionally to say so.
 */
function ClassroomCapsuleEmbed({
  content,
  capsuleUri,
}: {
  content: ClassroomContent
  capsuleUri: string
}) {
  const {postView, capsule} = useClassroomCapsule({entry: content, capsuleUri})
  return (
    <CapsuleFeedEmbed
      post={postView}
      capsule={capsule}
      capsuleUri={capsuleUri}
    />
  )
}

function Tag({children}: {children: React.ReactNode}) {
  const t = useTheme()
  return (
    <View
      style={[
        a.rounded_full,
        a.px_sm,
        {paddingVertical: 2},
        t.atoms.bg_contrast_25,
      ]}>
      <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text_contrast_high]}>
        {children}
      </Text>
    </View>
  )
}

const styles = StyleSheet.create({
  /** The same layout a post in a feed uses: avatar, then the content column. */
  layout: {
    flexDirection: 'row',
    gap: 10,
  },
  layoutAvi: {
    paddingLeft: 2,
  },
})
